/**
 * Plan ve kota sözleşmesi (M6 WP 6.1; K-M6-20; migration 0072/0073). Belge: docs/spec/castinflow/plans-quotas.md.
 *
 * - Plan kataloğu veritabanındadır (`plans`); buradaki sabitler yalnız ANAHTARLARI, etiketleri ve değerlendirme kurallarını
 *   tanımlar. Plan adları Castinflow'a özgüdür; ücret, ödeme ve kendi kendine plan değiştirme bu sözleşmede YOKTUR.
 * - Karar sunucudadır: kapalı özellik ve aşılan tavan `PLAN_LIMIT` (403), dolu depolama `STORAGE_FULL` (507) döner. Arayüz bu
 *   sözleşmeyi yalnız bilgilendirme için okur.
 * - Tavan `null` = o planda sınırsız. Sistem üst sınırı (paylaşılan sabitler) her zaman geçerlidir: plan ya da çalışma alanına
 *   özel tavan sistem üst sınırının üstüne çıkamaz.
 * - Kota düşünce mevcut veri silinmez: kullanım tavanın üstünde kalabilir (`over`); yalnız yeni oluşturma/yükleme reddedilir.
 */
import { z } from 'zod';
import { moduleDecisionsSchema, moduleLimitDetailsSchema } from './modules.js';
// ---------------------------------------------------------------------------------------------------------------------
// Anahtarlar
// ---------------------------------------------------------------------------------------------------------------------
/** Plan özellikleri (aç/kapa). Anahtar kapalıysa ilgili uçlar `PLAN_LIMIT` + `reason: 'feature_disabled'` döner. */
export const PLAN_FEATURES = [
    'custom_forms',
    'automation',
    'inbound_email',
    'webhooks',
    'api_tokens',
];
/** Sayılabilir kaynaklar (adet tavanı). Depolama ayrı tutulur (`storage_bytes`, bayt). */
export const PLAN_RESOURCES = [
    'users',
    'records',
    'sales_stages',
    'custom_forms',
    'automation_rules',
    'webhook_subscriptions',
    'api_tokens',
    'imap_accounts',
    // v3.1 Dalga 2A S7 (R0): sağlayıcı bağlantısı sayısı (kesilmemiş bağlantılar; ADR-0016 § 1 "bağlantı sayısı plan kotasıdır").
    // K2 (2026-10-11): reklam hesabı bağlantıları bu sayaca GİRMEZ — arayüzde "Diğer bağlantılar"
    'provider_connections',
    // K2: reklam hesabı alt kotası — kesilmemiş reklam hesabı bağlantıları (sosyal medya + arama reklamları BİRLİKTE; sağlayıcı
    // ayrımı yok). "Hangi bağlantı reklam hesabıdır" kuralı: `AD_ACCOUNT_CONNECTION_KINDS` (provider-connections.ts). Plan tavanı
    // `plans.limits` nesnesinde DEĞİL, `plans.ad_account_limit` sütunundadır (önceki sürümün kapalı şeması bilinmeyen anahtarı
    // reddeder); uygulama tavanları `plan_limits_resolved()` ile okur (migration 0190)
    'ad_accounts',
];
export const PLAN_STORAGE_KEY = 'storage_bytes';
/**
 * Aylık kullanım tavanları (v3.1 S7, U1): kullanım olay defterinden (`usage_events`) çalışma alanının TAKVİM ayına göre sayılır
 * (ay sınırı `tenant_settings.timezone` IANA saat diliminde; yaz saati geçişi veritabanının saat dilimi verisiyle). Adet
 * tavanı değil dönem tavanıdır: ay dönünce kullanım sıfırdan sayılır; veri silinmez.
 */
export const PLAN_MONTHLY_KEYS = ['outbound_messages_monthly', 'lead_events_monthly'];
export const PLAN_LIMIT_KEYS = [...PLAN_RESOURCES, PLAN_STORAGE_KEY, ...PLAN_MONTHLY_KEYS];
/** `quota_exceeded` ayrıntısının kaynağı: adet tavanı ya da aylık tavan */
export const PLAN_QUOTA_KEYS = [...PLAN_RESOURCES, ...PLAN_MONTHLY_KEYS];
/** Yeni çalışma alanının planı (`tenants.plan_id` varsayılanı) */
export const DEFAULT_PLAN_ID = 'trial';
export const planIdSchema = z.string().regex(/^[a-z][a-z0-9_]{1,30}$/);
export const PLAN_FEATURE_LABELS = {
    custom_forms: 'Özel formlar',
    automation: 'Otomasyon',
    inbound_email: 'Gelen e-posta',
    webhooks: 'Web kancaları',
    api_tokens: 'API anahtarları',
};
export const PLAN_LIMIT_LABELS = {
    users: 'Kullanıcı',
    records: 'Kayıt',
    sales_stages: 'Satış süreci aşaması',
    custom_forms: 'Özel form',
    automation_rules: 'Otomasyon kuralı',
    webhook_subscriptions: 'Web kancası aboneliği',
    api_tokens: 'Etkin API anahtarı',
    imap_accounts: 'Gelen e-posta hesabı',
    provider_connections: 'Diğer bağlantılar',
    ad_accounts: 'Reklam hesapları',
    storage_bytes: 'Dosya depolama',
    outbound_messages_monthly: 'Giden ileti (bu ay)',
    lead_events_monthly: 'Aday olayı (bu ay)',
};
/** Kaynağı açan özellik: özellik kapalıysa kaynak tavanı anlamsızdır (arayüz "planınızda yok" gösterir). */
export const PLAN_RESOURCE_FEATURE = {
    custom_forms: 'custom_forms',
    automation_rules: 'automation',
    webhook_subscriptions: 'webhooks',
    api_tokens: 'api_tokens',
    imap_accounts: 'inbound_email',
};
/**
 * Sistem üst sınırları: plan kataloğundan BAĞIMSIZ, her çalışma alanında geçerli teknik tavanlar. `null` = sistem tavanı tanımlı
 * değil (yalnız plan tavanı uygulanır). Değerler mevcut paylaşılan sabitlerle AYNIDIR (`AUTOMATION_LIMITS.maxRulesPerTenant`,
 * `WEBHOOK_MAX_SUBSCRIPTIONS`, `IMAP_LIMITS.maxAccountsPerTenant`, aşama kaydetme şemasının üst sınırı); eşitlik
 * `plans.test.ts` ile kilitlidir. Sabitler burada içe aktarılmaz: bu modül arayüz kabuğunun ilk paketine girer ve o
 * modüllerin şemalarını pakete taşımamalıdır.
 */
export const PLAN_SYSTEM_CAPS = {
    users: null,
    records: null,
    sales_stages: 30,
    custom_forms: null,
    automation_rules: 200,
    webhook_subscriptions: 20,
    api_tokens: null,
    imap_accounts: 10,
    // `PROVIDER_CONNECTIONS_MAX` (provider-connections.ts) ile aynı; eşitlik plans.test.ts'te kilitli
    provider_connections: 50,
    // `AD_ACCOUNTS_MAX` ile aynı (ayrılmadan önceki fiilî üst sınır korunur); eşitlik plans.test.ts'te kilitli
    ad_accounts: 50,
    storage_bytes: null,
    outbound_messages_monthly: null,
    lead_events_monthly: null,
};
// ---------------------------------------------------------------------------------------------------------------------
// Katalog satırı
// ---------------------------------------------------------------------------------------------------------------------
const limitValue = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).nullable();
export const planFeaturesSchema = z
    .object({
    custom_forms: z.boolean(),
    automation: z.boolean(),
    inbound_email: z.boolean(),
    webhooks: z.boolean(),
    api_tokens: z.boolean(),
})
    .strict();
export const planLimitsSchema = z
    .object({
    users: limitValue,
    records: limitValue,
    sales_stages: limitValue,
    custom_forms: limitValue,
    automation_rules: limitValue,
    webhook_subscriptions: limitValue,
    api_tokens: limitValue,
    imap_accounts: limitValue,
    provider_connections: limitValue,
    ad_accounts: limitValue,
    storage_bytes: limitValue,
    outbound_messages_monthly: limitValue,
    lead_events_monthly: limitValue,
})
    .strict();
export const planSchema = z.object({
    id: planIdSchema,
    name: z.string().min(1).max(60),
    description: z.string().max(300),
    rank: z.number().int(),
    features: planFeaturesSchema,
    limits: planLimitsSchema,
});
// ---------------------------------------------------------------------------------------------------------------------
// Değerlendirme (saf; sunucu ve arayüz ortak)
// ---------------------------------------------------------------------------------------------------------------------
/** İki tavandan küçüğü (`null` = sınırsız). */
export function minLimit(a, b) {
    if (a === null)
        return b;
    if (b === null)
        return a;
    return Math.min(a, b);
}
/** Etkin tavan: çalışma alanına özel tavan varsa o, yoksa plan tavanı; sonuç sistem üst sınırını aşamaz. */
export function effectiveLimit(limits, overrides, key) {
    const override = overrides[key];
    const overridden = override !== undefined;
    return {
        limit: minLimit(overridden ? override : limits[key], PLAN_SYSTEM_CAPS[key]),
        overridden,
    };
}
export function planAllows(features, feature) {
    return features[feature];
}
/** Kullanım durumu: `near` ≥ %80, `at` = tavan, `over` > tavan (kota düşürülmüşse oluşur). Sınırsızda her zaman `ok`. */
export const PLAN_USAGE_STATES = ['ok', 'near', 'at', 'over'];
export const PLAN_NEAR_RATIO = 0.8;
export function usageState(used, limit) {
    if (limit === null)
        return 'ok';
    if (used > limit)
        return 'over';
    if (used === limit)
        return 'at';
    return used >= limit * PLAN_NEAR_RATIO ? 'near' : 'ok';
}
/** Yeni `add` birim tavana sığıyor mu? Kullanım zaten tavanın üstündeyse hiçbir artış sığmaz (`add = 0` sığar). */
export function fitsLimit(used, add, limit) {
    if (limit === null || add <= 0)
        return true;
    return used + add <= limit;
}
/** Kalan yer: `null` = sınırsız; kullanım tavanın üstündeyse (kota düşürülmüş / önceden bağlanmış fazlalık) 0. */
export function remainingOf(used, limit) {
    return limit === null ? null : Math.max(0, limit - used);
}
/**
 * Tek kaynağın anlık kota durumu (bilgilendirme; karar sunucudaki kapıdadır): kullanılan / etkin tavan / kalan yer.
 * `limit` ve `remaining` `null` = sınırsız.
 */
export const planQuotaStatusSchema = z.object({
    used: z.number().int(),
    limit: z.number().int().nullable(),
    remaining: z.number().int().nullable(),
});
// ---------------------------------------------------------------------------------------------------------------------
// Hata ayrıntıları (`error.details`)
// ---------------------------------------------------------------------------------------------------------------------
const planRefSchema = z.object({ id: planIdSchema, name: z.string() });
/** `PLAN_LIMIT` (403) ayrıntısı: hangi özellik kapalı ya da hangi tavan aşıldı. */
export const planLimitDetailsSchema = z.discriminatedUnion('reason', [
    z.object({
        reason: z.literal('feature_disabled'),
        feature: z.enum(PLAN_FEATURES),
        plan: planRefSchema,
    }),
    z.object({
        reason: z.literal('quota_exceeded'),
        resource: z.enum(PLAN_QUOTA_KEYS),
        limit: z.number().int(),
        used: z.number().int(),
        requested: z.number().int(),
        /** Kalan yer (`max(0, limit − used)`): istek bütünüyle reddedildi; en çok bu kadarı sığardı. Eski sunucu yanıtında yoktur */
        remaining: z.number().int().optional(),
        plan: planRefSchema,
    }),
    // v4.5 A-0: modül kapısı (`modules.ts`). Eski sunucu bu kolu üretmez
    moduleLimitDetailsSchema.extend({ plan: planRefSchema }),
]);
/** `STORAGE_FULL` (507) ayrıntısı */
export const storageFullDetailsSchema = z.object({
    reason: z.literal('storage_quota'),
    quotaBytes: z.number().int(),
    usedBytes: z.number().int(),
    requestedBytes: z.number().int(),
    plan: planRefSchema,
});
// ---------------------------------------------------------------------------------------------------------------------
// API sözleşmesi
// ---------------------------------------------------------------------------------------------------------------------
/** `GET /plan` — oturumdaki her kullanıcı: plan adı ve özellik kararları (arayüz kapıları bunu okur). */
export const planSummarySchema = z.object({
    plan: z.object({ id: planIdSchema, name: z.string(), description: z.string() }),
    /** Demo çalışma alanı: plan kapılarından BAĞIMSIZ ek kısıt (dış etkiler kapalı) */
    isDemo: z.boolean(),
    features: planFeaturesSchema,
    /** v4.5 A-0: etkin modül kararları (`modules.ts`). Eski sunucu yanıtında alan yoktur → arayüz modülü kapalı sayar */
    modules: moduleDecisionsSchema.optional(),
});
export const planUsageItemSchema = z.object({
    key: z.enum(PLAN_LIMIT_KEYS),
    /** `null` = sınırsız */
    limit: z.number().int().nullable(),
    used: z.number().int(),
    /** Çalışma alanına özel tavan (operatör ataması) */
    overridden: z.boolean(),
    state: z.enum(PLAN_USAGE_STATES),
    /** Kaynağı açan özellik bu planda kapalıysa `false` (tavan gösterilmez) */
    available: z.boolean(),
    /** Aylık tavan (v3.1 S7): kullanım içinde bulunulan takvim ayına aittir. Eski sunucu yanıtında alan yoktur. */
    period: z.literal('month').optional(),
});
/** `GET /plan/usage` — yalnız `settings.manage`: tavanlar, güncel kullanım, aşım durumu. */
export const planUsageSchema = planSummarySchema.extend({
    usage: z.array(planUsageItemSchema),
    /** En kötü durum (uyarı bandı için) */
    worst: z.enum(PLAN_USAGE_STATES),
    /** Son atama (operatör); hiç atanmadıysa `null` */
    assignedAt: z.string().nullable(),
});
const STATE_ORDER = { ok: 0, near: 1, at: 2, over: 3 };
export function worstUsageState(states) {
    let worst = 'ok';
    for (const s of states)
        if (STATE_ORDER[s] > STATE_ORDER[worst])
            worst = s;
    return worst;
}
//# sourceMappingURL=plans.js.map